import {
  BaseTrackableObject,
  TrackChangeOfString,
  TrackChangeOfNullableNumber,
  TrackChangeOfNullableBoolean,
  TrackChangeOfNumber,
  TrackChangeOfDate,
  TrackChangeOfBoolean,
} from './trackable-value.interface';
import { IPolicyHolderDetails } from './policyholder.interface';
import { IMotorQuote } from './motor-quote.interface';
import { IHomeAndContentsQuote } from './home-contents-quote.interface';
import { PaymentTypes } from '@/enums/payment-types.enum';
import { IPetsQuote } from './pet.interface';

export interface IQuote extends BaseTrackableObject {
  quoteType?: InsuranceQuoteType;
  quotePhase: InsuranceQuotePhase;
  paymentFrequency?: PaymentFrequency;
  paymentType?: PaymentTypes;
  paymentReceiptNumber: string;
  paymentReferenceNumber: string;
  paymentDueDate?: Date;
  id: string;
  reference: string;
  clientIpAddress: string;
  policyReference: string;
  externalId: string;
  producerCode: TrackChangeOfString;
  motorQuote: IMotorQuote;
  homeAndContentsQuote: IHomeAndContentsQuote;
  petsQuote: Partial<IPetsQuote>;
  discounts: QuoteDiscounts;
  primaryPolicyHolderDetails: IPolicyHolderDetails;
  additionalPolicyHolderDetails: IPolicyHolderDetails[];
  additionalPolicyHolderDetailsCount: TrackChangeOfNumber;
  insuranceStartDate: TrackChangeOfDate;
  termStartDate?: Date;
  termEndDate?: Date;
  inceptionDate: Date;
  insuranceQuoteSummary: InsuranceQuoteSummary;
  coverageSummaries: CoverageSummary[];
  ruleCalculations: InsuranceQuoteRuleCalculations;
  dutyOfDisclosureAccepted: TrackChangeOfBoolean;
  membershipTermsAndConditionsAccepted: TrackChangeOfNullableBoolean;
  matchConfidence: string;
  renewalPreference: boolean;
  renewalTermAcceptance: boolean;
  rejectionReason: string;
}

export interface InsuranceQuoteSummary extends PricingSummary {
  monthlyAnnualPremium?: number;
  monthlyInstalment: number;
  payByTheMonthFee: number;
  bestPremium: number;
  policySystemPremium?: number;
  validToDate?: Date;
}

export interface PricingSummary {
  yearlyCallCenterPremium: number;
  yearlyAnnualPremium: number;
  monthlyCallCenterDeposit: number;
  monthlyDeposit: number;
  calculatedDate?: Date;
}

export interface PremiumDetails {
  calculationOrder: number;
  pointsUsed: number;
  percentageUsed: number;
  premiumValue: number;
  dailyPremiumValue?: number;
  coverageGroup?: number;
  description: string;
}

export interface QuoteDiscounts extends BaseTrackableObject {
  eligibleForMultiPolicyDiscount: TrackChangeOfBoolean;
  eligibleForCombinedPolicyDiscount: TrackChangeOfBoolean;
  eligibleForLoyaltyDiscount: TrackChangeOfBoolean;
  highestMembershipLoyaltyLevel: TrackChangeOfString;
  highestRoadsideProduct: TrackChangeOfString;
  eligibleForYearlyOnlineDiscount: TrackChangeOfBoolean;
  eligibleForMonthlyOnlineDiscount: TrackChangeOfBoolean;
  eligibleForSeniorCardDiscount: TrackChangeOfBoolean;
  eligibleForSeniorDiscount: TrackChangeOfBoolean;
  totalYearlyOnlineDiscountAmount: TrackChangeOfNullableNumber;
  totalMonthlyOnlineDiscountAmount: TrackChangeOfNullableNumber;
  policySystemDiscounts: PolicySystemDiscounts;
  eligibleForHomeBundleDiscount: TrackChangeOfBoolean;
  eligibleForStaffDiscount: TrackChangeOfBoolean;
  staffCategory: TrackChangeOfString;
}

export interface PolicySystemDiscounts extends BaseTrackableObject {
  ageDiscount?: boolean;
  alarmDiscount?: boolean;
  buildingTypeDiscount?: boolean;
  combinedDiscount?: boolean;
  driverExcellenceBonusDiscount?: boolean;
  householdExcellenceBonusDiscount?: boolean;
  multiPetDiscount?: boolean;
  multipleProductDiscount?: boolean;
  noClaimDiscount?: boolean;
  roadSideAssistanceLoyaltyDiscount?: boolean;
  seniorCardHolderDiscount?: boolean;
  staffDiscount?: boolean;
}

export interface CoverageSummary extends PricingSummary {
  coverageSummaryId: string;
  premiumDetails: PremiumDetails[];
}

export interface InsuranceQuoteRuleCalculations extends BaseTrackableObject {
  automationLevel: number;
  manualProcessingReasons: ManualProcessingReason[];
  prerenewalDirections: PrerenewalDirection[];
  payByTheMonthFeeWaived?: boolean;
  premiumLowerThanQuoted?: boolean;
  invalidRiskLocation?: boolean;
  discountDifferences: DiscountDifference[];
}

export interface ManualProcessingReason {
  code: number;
  description: string;
}

export interface PrerenewalDirection {
  code: string;
}

export interface DiscountDifference {
  discountType: number;
  iqHasDiscount: boolean;
  policySystemHasDiscount: boolean;
}

export enum InsuranceQuoteType {
  MotorComprehensive = 1,
  All = 2,
  Home = 3,
  Contents = 4,
  HomeAndContents = 5,
  MotorThirdPartyLiability = 6,
  MotorFireTheftAndThirdPartyLiability = 7,
  PetComp = 8,
  Caravan = 9,
  Motorcycle = 10,
  BoatComprehensive = 11,
  BoatThirdParty = 12,
  MotorThirdPartyLiabilityOther = 13, //MotorThirdPartyLiabilityOther
  MotorFireTheftAndThirdPartyLiabilityOther = 14, //MotorFireTheftAndThirdPartyLiabilityOther
  MotorComprehensiveOther = 15, //MotorComprehensiveOther
  MotorClassic = 16, //Classic motor
}

export enum InsuranceQuotePhase {
  Unknown = 0,
  QuickQuote = 1,
  FullQuote = 2,
  MidtermPolicyChange = 3,
  RenewalPolicyChange = 4,
}

export enum PaymentFrequency {
  Annually = 1,
  Monthly = 2,
}

export enum ChangeState {
  Unchanged = 0,
  Changed = 1,
  BusinessRuleComplete = 2,
  UsedInQuote = 3,
}

export enum OnlineServicesQuoteType {
  All = 0,
  MC = 1,
  MT = 2,
  MF = 3,
  PET = 4,
  HHCOMB = 5,
  HH = 6,
  HC = 7,
}

export const mapInsuranceQuoteTypeToOnlineServicesQuoteType = (quoteTypeCode: string): string => {
  let onlineServicesQuoteType;

  switch (Number(quoteTypeCode)) {
    case InsuranceQuoteType.MotorComprehensive:
    case InsuranceQuoteType.MotorComprehensiveOther:
      onlineServicesQuoteType = OnlineServicesQuoteType.MC;
      break;
    case InsuranceQuoteType.MotorThirdPartyLiability:
    case InsuranceQuoteType.MotorThirdPartyLiabilityOther:
      onlineServicesQuoteType = OnlineServicesQuoteType.MT;
      break;
    case InsuranceQuoteType.MotorFireTheftAndThirdPartyLiability:
    case InsuranceQuoteType.MotorFireTheftAndThirdPartyLiabilityOther:
      onlineServicesQuoteType = OnlineServicesQuoteType.MF;
      break;
    case InsuranceQuoteType.PetComp:
      onlineServicesQuoteType = OnlineServicesQuoteType.PET;
      break;
    case InsuranceQuoteType.HomeAndContents:
      onlineServicesQuoteType = OnlineServicesQuoteType.HHCOMB;
      break;
    case InsuranceQuoteType.Home:
      onlineServicesQuoteType = OnlineServicesQuoteType.HH;
      break;
    case InsuranceQuoteType.Contents:
      onlineServicesQuoteType = OnlineServicesQuoteType.HC;
      break;
    case InsuranceQuoteType.All:
      onlineServicesQuoteType = OnlineServicesQuoteType.All;
      break;
    default:
      onlineServicesQuoteType = -1;
      break;
  }

  return onlineServicesQuoteType > -1 ? onlineServicesQuoteType.toString() : '';
};

// Map Descriptive Enum to Codes
export const QuoteTypeCodes: { [key in InsuranceQuoteType]: string } = {
  [InsuranceQuoteType.MotorComprehensive]: 'MC',
  [InsuranceQuoteType.All]: 'All',
  [InsuranceQuoteType.Home]: 'HH',
  [InsuranceQuoteType.Contents]: 'HC',
  [InsuranceQuoteType.HomeAndContents]: 'HHCOMB',
  [InsuranceQuoteType.MotorThirdPartyLiability]: 'MT',
  [InsuranceQuoteType.MotorFireTheftAndThirdPartyLiability]: 'MF',
  [InsuranceQuoteType.PetComp]: 'PET',
  [InsuranceQuoteType.Caravan]: 'Caravan',
  [InsuranceQuoteType.Motorcycle]: 'Motorcycle',
  [InsuranceQuoteType.BoatComprehensive]: 'BC',
  [InsuranceQuoteType.BoatThirdParty]: 'BT',
  [InsuranceQuoteType.MotorThirdPartyLiabilityOther]: 'MTOther',
  [InsuranceQuoteType.MotorFireTheftAndThirdPartyLiabilityOther]: 'MFOther',
  [InsuranceQuoteType.MotorComprehensiveOther]: 'MCOther',
  [InsuranceQuoteType.MotorClassic]: 'Classic',
};

export enum GroupedQuoteType {
  Unknown = 0,
  Motor = 1,
  Home = 2,
  Pet = 3,
  Motorcycle = 4,
  Boat = 5,
  Caravan = 6,
}

export const getGroupedQuoteType = (quoteTypeCode: string): GroupedQuoteType => {
  switch (quoteTypeCode) {
    case QuoteTypeCodes[InsuranceQuoteType.MotorComprehensive]:
    case QuoteTypeCodes[InsuranceQuoteType.MotorThirdPartyLiability]:
    case QuoteTypeCodes[InsuranceQuoteType.MotorFireTheftAndThirdPartyLiability]:
      return GroupedQuoteType.Motor;
    case QuoteTypeCodes[InsuranceQuoteType.Home]:
    case QuoteTypeCodes[InsuranceQuoteType.Contents]:
    case QuoteTypeCodes[InsuranceQuoteType.HomeAndContents]:
      return GroupedQuoteType.Home;
    case QuoteTypeCodes[InsuranceQuoteType.PetComp]:
      return GroupedQuoteType.Pet;
    case QuoteTypeCodes[InsuranceQuoteType.Motorcycle]:
      return GroupedQuoteType.Motorcycle;
    case QuoteTypeCodes[InsuranceQuoteType.BoatComprehensive]:
    case QuoteTypeCodes[InsuranceQuoteType.BoatThirdParty]:
      return GroupedQuoteType.Boat;
    case QuoteTypeCodes[InsuranceQuoteType.Caravan]:
      return GroupedQuoteType.Caravan;
    default:
      return GroupedQuoteType.Unknown;
  }
};

export const getInsuranceTypeDisplayName = (
  dictionary: any,
  quoteType: string,
  convertToLowerCase: boolean = false
) => {
  let value = '';
  switch (quoteType) {
    case QuoteTypeCodes[InsuranceQuoteType.Contents]:
      value = dictionary.IT_HC;
      break;
    case QuoteTypeCodes[InsuranceQuoteType.Home]:
      value = dictionary.IT_HH;
      break;
    case QuoteTypeCodes[InsuranceQuoteType.HomeAndContents]:
      value = dictionary.IT_HHCOMB;
      break;
    case QuoteTypeCodes[InsuranceQuoteType.MotorComprehensive]:
      value = dictionary.IT_MC;
      break;
    case QuoteTypeCodes[InsuranceQuoteType.MotorComprehensiveOther]:
      value = dictionary.IT_MCOther;
      break;
    case QuoteTypeCodes[InsuranceQuoteType.MotorFireTheftAndThirdPartyLiability]:
    case QuoteTypeCodes[InsuranceQuoteType.MotorFireTheftAndThirdPartyLiabilityOther]:
      value = dictionary.IT_MF;
      break;
    case QuoteTypeCodes[InsuranceQuoteType.MotorThirdPartyLiability]:
    case QuoteTypeCodes[InsuranceQuoteType.MotorThirdPartyLiabilityOther]:
      value = dictionary.IT_MT;
      break;
    case QuoteTypeCodes[InsuranceQuoteType.PetComp]:
      value = dictionary.IT_PET;
      break;
    case QuoteTypeCodes[InsuranceQuoteType.Motorcycle]:
      value = dictionary.IT_Motorcycle;
      break;
    case QuoteTypeCodes[InsuranceQuoteType.Caravan]:
      value = dictionary.IT_Caravan;
      break;
    case QuoteTypeCodes[InsuranceQuoteType.BoatComprehensive]:
      value = dictionary.IT_BC;
      break;
    case QuoteTypeCodes[InsuranceQuoteType.BoatThirdParty]:
      value = dictionary.IT_BT;
      break;
    case QuoteTypeCodes[InsuranceQuoteType.MotorClassic]:
      value = dictionary.IT_Classic;
      break;
  }

  return convertToLowerCase ? value.toLowerCase() : value;
};

export enum OwnerOccupancyCode {
  OWNEROCC = 'OWNEROCC',
  LANDLORD = 'LANDLORD',
  TENANTPERM = 'TENANTPERM',
}

export const getOccupancyDisplayName = (occupancyCode: OwnerOccupancyCode) => {
  switch (occupancyCode) {
    case OwnerOccupancyCode.LANDLORD:
      return 'Landlord';
    case OwnerOccupancyCode.OWNEROCC:
      return 'Owner Occupied';
    case OwnerOccupancyCode.TENANTPERM:
      return 'Renter';
    default:
      return '';
  }
};
